import { AsyncPipe, NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { LabelComponent } from "@components/label/label.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { isNil } from "lodash";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, map, takeUntil, withLatestFrom } from "rxjs/operators";
import { IMapComponent } from "~app/common/components/map/map.models";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DisabledControlDirective } from "../../../../common/directives/disabled-control/disabled-control.directive";
import { Atgard } from "../../models/atgard-models";
import { Geometri } from "../../models/karta-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationRuleDirective } from "../../validation/directives/virkeskop-validation-rule.directive";

interface Drivningspunkter{
    avlaggUtanTillganglighetsKlass: Geometri[];
    isAvlaggInvalid: boolean;
    isAvlaggMissing: boolean;
    isLarmkoordinatInvalid: boolean;
    larmpunkter: Geometri[];
}

@Component({
    selector: "rask-atgard-transport",
    templateUrl: "./atgard-transport.component.html",
    styleUrls: ["./atgard-transport.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextmultilineComponent,
        NgClass,
        SimpleSelectorComponent,
        VirkeskopValidationRuleDirective,
        FormsModule,
        DisabledControlDirective,
        LabelComponent,
        AsyncPipe
    ]
})
export class AtgardTransportComponent implements OnInit, OnDestroy {
    map: IMapComponent;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly destroy$ = new Subject<void>();
    private readonly drivningspunkterSrc = new BehaviorSubject<Drivningspunkter>({
        isAvlaggInvalid: true,
        isLarmkoordinatInvalid: true,
        isAvlaggMissing: true,
        avlaggUtanTillganglighetsKlass: null,
        larmpunkter: null
    });
    private readonly tillganglighetVagChangeSrc = new Subject<string>();
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    protected get atgard$(): Observable<Atgard> {
        return this.atgardMediatorService.atgard$;
    }

    protected get drivningspunkter$(): Observable<Drivningspunkter> {
        return this.drivningspunkterSrc.asObservable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    ngOnInit(): void {
        this.vkKartaMediatorService.atgardGeometrier$
            .pipe(
                withLatestFrom(this.atgard$),
                map(([
                    geometrier,
                    atgard
                ]) => geometrier?.find(geometri => geometri.atgardGuid === atgard.guid)),
                takeUntil(this.destroy$))
            .subscribe(geometri => {
                if(ObjectUtils.hasValue(geometri)) {
                    this.drivningspunkterSrc.next({
                        isAvlaggInvalid: geometri.isAvlaggInvalid,
                        isLarmkoordinatInvalid: geometri.isLarmkoordinatInvalid,
                        isAvlaggMissing: geometri.isAvlaggMissing,
                        avlaggUtanTillganglighetsKlass: geometri.avlagg?.filter(x => !x.kartgeometri.tillganglighetsklass),
                        larmpunkter: geometri.larmpunkter
                    });
                }
            });

        this.tillganglighetVagChangeSrc
            .pipe(
                withLatestFrom(this.atgard$),
                filter(([value]) => !isNil(value)),
                takeUntil(this.destroy$))
            .subscribe(([
                tillganglighetVag,
                atgard
            ]) => {
                atgard.tillganglighetVag = tillganglighetVag;
                this.atgardMediatorService.update(atgard);
            });
    }

    protected tillganglighetVagChange(value: string) {
        this.tillganglighetVagChangeSrc.next(value);
    }

    protected viewInMap($event: Event, geometri: Geometri): void {
        $event.preventDefault();
        this.vkKartaMediatorService.openPopupGeometri(geometri.mapGeometry);
    }
}
